import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CategoryField } from 'categories/components';
import { Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { PricingProfileRuleType } from 'pricing/enums';
import { IPricingProfileRule } from 'pricing/types';

import { CompanyPartSpecificationField, ManufacturerField, PartSpecificationField } from './fields';
import { RemovePricingProfileRulesButton } from './RemovePricingProfileRulesButton';
import { IPricingProfileRuleForm } from './types';

interface IProps {
  type: PricingProfileRuleType;
  pricingProfileRule?: IPricingProfileRule;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    reset?: UseFormReset<IPricingProfileRuleForm>
  ) => (data: IPricingProfileRuleForm) => Promise<void>;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfileRuleFormFields = ({
  type,
  pricingProfileRule,
  isOpen,
  onClose,
  onSubmit,
  onRemoveSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const title = useMemo(() => {
    if (pricingProfileRule) {
      return type === PricingProfileRuleType.MARGIN
        ? t('titleUpdateMarginRule')
        : t('titleUpdateDiscountRule');
    }

    return type === PricingProfileRuleType.MARGIN
      ? t('titleAddMarginRule')
      : t('titleAddDiscountRule');
  }, [pricingProfileRule, type, t]);

  const valueLabel = type === PricingProfileRuleType.MARGIN ? t('labelMargin') : t('labelDiscount');

  const removeButton = useMemo(() => {
    return pricingProfileRule ? (
      <RemovePricingProfileRulesButton
        pricingProfileRules={[pricingProfileRule]}
        buttonLabel={t('buttonLabelRemovePricingProfileRule')}
        onRemoveSuccess={onRemoveSuccess}
        notificationPosition={NotificationPosition.FORM}
      />
    ) : null;
  }, [pricingProfileRule, onRemoveSuccess, t]);

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      extraButtons={removeButton}
    >
      <ManufacturerField name="manufacturer" />

      <CategoryField name="category" label={t('labelCategory')} placeholder={t('labelAll')} />

      <PartSpecificationField name="partFieldCriteria" />

      <CompanyPartSpecificationField name="companyPartFieldCriteria" />

      <FormField
        name="value"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: valueLabel,
          required: true,
          endDecorator: '%',
        }}
      />
    </Form>
  );
};
